import React from 'react';
import { last, cloneDeep } from 'lodash';
import { dateService } from '../../../services/date';
import { Columns } from '../../base/megatable/column-service';
import Tooltip from '../../misc/tooltip';
import linkoutIcon from '../../../images/icons/linkout.svg';

const EVERGREEN_STRATEGY = 9999999999;

const columnService = new Columns();

const chartOptions = [
  {
    label: 'Clicks',
    statKey: 'clicks',
    dataType: 'number',
    d3Format: '',
    rowColor: true,
  },
  {
    label: 'Revenue',
    statKey: 'attributed_revenue',
    dataType: 'currency',
    d3Format: '$',
    rowColor: true,
  },
];

const dataMap = {
  publishers: {
    url: (orgId, strategyId) => {
      return `/api/v0/merchants/${orgId}/strategies/${strategyId}/pubs_performance/`;
    },
    title: 'Publishers',
    columns: columnService.strategyTopPublishersFields,
    interval_key: 'pub_id',
    name_key: 'pub_name',
    graph_param_key: 'pub_ids',
    static_fields: ['pub_name'],
    pub_dropdown: true,
  },
  stories: {
    url: (orgId, strategyId) => {
      return `/api/v0/merchants/${orgId}/strategies/${strategyId}/stories_performance/`;
    },
    title: 'Stories',
    columns: columnService.strategyTopStoriesFields,
    interval_key: 'edit_id',
    name_key: 'story_name',
    graph_param_key: 'edit_ids',
    static_fields: ['pub_name', 'story_name'],
    pub_dropdown: true,
  },
  recommendations: {
    url: (orgId, strategyId) => {
      return `/api/v0/merchants/${orgId}/strategies/${strategyId}/recommendations_performance/`;
    },
    title: 'Recommendations',
    columns: columnService.strategyTopRecommendationsFields,
    interval_key: 'auction_id',
    name_key: 'brand_product_name',
    graph_param_key: 'auction_ids',
    static_fields: ['pub_name', 'story_name', 'brand_product_name'],
    pub_dropdown: false,
  },
  products: {
    columns: columnService.strategyTopProductsFields,
  },
};

const getCheckbox = (dataKey, type, columnObj, props) => {
  return (
    <div key={`${type}-checkbox-${props.data[dataKey]}`} className={`column ${columnObj.name}`}>
      <div
        className={`checkbox ${props.checkboxClass}`}
        onClick={() => props.updateGraphedState(props.data)}
      />
      <p>{props.data[columnObj.name]}</p>
    </div>
  );
};

const columnFormatters = {
  publishers: (columnObj, props = {}, history) => {
    if (columnObj.name === 'pub_name') {
      return getCheckbox('pub_id', 'publishers', columnObj, props);
    }
  },
  stories: (columnObj, props = {}, history) => {
    if (columnObj.name === 'pub_name') {
      return getCheckbox('edit_id', 'stories', columnObj, props);
    }

    if (columnObj.name === 'story_name') {
      return (
        <div className="column story_name" key={`story_name ${props.data.story_name}`}>
          <p
            ref={node =>
              new Tooltip(node, {
                text: 'View Top Recommendations for this Story',
                size: 180,
                fixedSize: true,
              })
            }
            onClick={() => {
              props.setStrategyCrumb(props.data, 'recommendations');
              const newUrl = window.location.pathname.replace('stories', 'recommendations');
              history.push(newUrl);
            }}
          >
            {props.data.story_name}
          </p>
          <a
            className="link-out"
            href={props.data.story_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={linkoutIcon} alt="link-out" />
          </a>
        </div>
      );
    }
  },
};

function isEvergreen(id) {
  return parseInt(id) === EVERGREEN_STRATEGY;
}

function determineGranularities(dateRange, granularities) {
  // Update granularity options on date change
  const numDays = dateService.subtractDates(dateRange.endDate, dateRange.startDate, 'day');
  const updatedGranularities = cloneDeep(granularities);

  updatedGranularities.forEach(item => {
    item.disabled =
      (item.name === 'weekly' && numDays < 14) ||
      (item.name === 'monthly' && numDays < 60) ||
      (item.name === 'yearly' && numDays < 365);

    if (item.disabled) {
      item.selected = false;
    }
  });

  if (!updatedGranularities.filter(item => item.selected).length) {
    // Select the last (largest) granularity that isn't disabled
    last(updatedGranularities.filter(item => !item.disabled)).selected = true;
  }

  return updatedGranularities;
}

function getChartOptions(type) {
  if (type === 'products') {
    return [chartOptions[1]];
  }
  return chartOptions;
}

export { isEvergreen, determineGranularities, getChartOptions, columnFormatters, dataMap };
